/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import { LinkButton, Text, TEXT_VIEW } from '@veeam/components';
import { observer } from 'mobx-react-lite';

// eslint-disable-next-line import/no-cycle
import { useGridApi } from '../../hooks';
import { SelectionMode } from '../../enums';
import { SelectionHeaderWrapper } from './components/SelectionHeaderWrapper';
import { AllButtonWrapper } from './components/AllButtonWrapper';
import { BoldText } from './components/BoldText';

import type { GridStoreImpl } from '../../stores';

export const SelectionHeader = observer(() => {
    const store = useGridApi() as GridStoreImpl<unknown, unknown, unknown>;
    const total = store.selection.fullLength;
    const selected = store.selected;
    const pagesCount = store.pagination.fullPages;

    if (store.paginationStore.useCompactPagination || store.paginationStore.useCompactPaginationWithCurrentPage) {
        return (
            <SelectionHeaderWrapper selectionMode={store.selectionMode}>
                <Text
                    view={TEXT_VIEW.secondary}
                    data-selector-id='SelectionHeaderText'
                >
                    {store.selection.isSelectedAll ? (
                        <>
                            {'Selected: '}

                            <BoldText>
                                {`All ${store.pageData.length} items`}
                            </BoldText>
                        </>
                    ) : (
                        <>
                            {'Selected: '}
                            {selected.length > 0 ? <BoldText>{selected.length}</BoldText> : selected.length}
                        </>
                    )}
                </Text>
            </SelectionHeaderWrapper>
        );
    }

    if (store.selectionMode === SelectionMode.CurrentPage) {
        return (
            <SelectionHeaderWrapper selectionMode={store.selectionMode}>
                <Text
                    view={TEXT_VIEW.secondary}
                    data-selector-id='SelectionHeaderText'
                >
                    {store.selection.isSelectedAll && pagesCount === 1 ? (
                        <>
                            {'Selected: '}

                            <BoldText>
                                {`All ${total} items`}
                            </BoldText>
                        </>
                    ) : (
                        <>
                            {'Selected: '}
                            {selected.length > 0 ? <BoldText>{selected.length}</BoldText> : selected.length}
                            {` of ${total}`}
                        </>
                    )}
                </Text>

                {store.selection.isSelectedAll &&
                store.paginationStore.withPagination &&
                pagesCount > 1 &&
                !store.selectionStore.disableAllPageSelection && (
                    <AllButtonWrapper>
                        <LinkButton
                            compact
                            onClick={store.selectAllOnAllPages}
                            key='selectAllButton'
                            data-selector-id='SelectAllButton'
                        >
                            {`Select All ${total} Items`}
                        </LinkButton>
                    </AllButtonWrapper>
                )}
            </SelectionHeaderWrapper>
        );
    }

    if (store.selectionMode === SelectionMode.AllPages) {
        const deselectedRows = store.pageData.length - store.selected.length;

        return (
            <SelectionHeaderWrapper selectionMode={store.selectionMode}>
                <Text
                    view={TEXT_VIEW.secondary}
                    data-selector-id='SelectionHeaderText'
                >
                    {store.selection.isSelectedAll ? (
                        <>
                            {'Selected: '}

                            <BoldText>
                                {`All ${total} items`}
                            </BoldText>
                        </>
                    ) : (
                        <>
                            {'Selected: '}
                            <BoldText>{total - deselectedRows}</BoldText>
                            {` of ${total}`}
                        </>
                    )}
                </Text>

                <AllButtonWrapper>
                    <LinkButton
                        compact
                        onClick={store.deselectAll}
                        key='deselectAllButton'
                        data-selector-id='DeselectAllButton'
                    >
                        Deselect All
                    </LinkButton>
                </AllButtonWrapper>
            </SelectionHeaderWrapper>
        );
    }
});
